import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names using clsx and tailwind-merge
 * 
 * This utility merges Tailwind CSS classes intelligently, handling conflicts
 * by keeping the last class when duplicates exist (e.g., "p-4 p-2" → "p-2")
 * 
 * @param inputs - Class names to combine (strings, arrays, objects)
 * @returns Merged class name string
 * 
 * @example
 * ```typescript
 * cn('px-4 py-2', 'bg-primary', { 'text-white': true })
 * // → 'px-4 py-2 bg-primary text-white'
 * 
 * cn('p-4', 'p-2') // → 'p-2' (conflict resolved)
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a byte value into a human-readable string
 * 
 * @param bytes - Number of bytes to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB")
 * 
 * @example
 * ```typescript
 * formatBytes(1024) // → '1 KB'
 * formatBytes(1536000) // → '1.46 MB'
 * formatBytes(0) // → '0 Bytes'
 * ```
 */
export function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Get US Federal Holidays for a given year
 * 
 * @param year - The year to get holidays for
 * @returns Array of holiday dates
 */
export function getUSFederalHolidays(year: number): Date[] {
  const holidays: Date[] = [];
  
  // New Year's Day - January 1
  holidays.push(new Date(year, 0, 1));
  
  // MLK Day - Third Monday in January
  holidays.push(getNthWeekdayOfMonth(year, 0, 1, 3));
  
  // Presidents Day - Third Monday in February
  holidays.push(getNthWeekdayOfMonth(year, 1, 1, 3));
  
  // Memorial Day - Last Monday in May
  holidays.push(getLastWeekdayOfMonth(year, 4, 1));
  
  // Independence Day - July 4
  holidays.push(new Date(year, 6, 4));
  
  // Labor Day - First Monday in September
  holidays.push(getNthWeekdayOfMonth(year, 8, 1, 1));
  
  // Columbus Day - Second Monday in October
  holidays.push(getNthWeekdayOfMonth(year, 9, 1, 2));
  
  // Veterans Day - November 11
  holidays.push(new Date(year, 10, 11));
  
  // Thanksgiving - Fourth Thursday in November
  holidays.push(getNthWeekdayOfMonth(year, 10, 4, 4));
  
  // Christmas - December 25
  holidays.push(new Date(year, 11, 25));
  
  return holidays;
}

/**
 * Helper to get the nth occurrence of a weekday in a month
 */
function getNthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  const offset = (weekday - firstWeekday + 7) % 7;
  const day = 1 + offset + (n - 1) * 7;
  return new Date(year, month, day);
}

/**
 * Helper to get the last occurrence of a weekday in a month
 */
function getLastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  const lastDay = new Date(year, month + 1, 0);
  const lastWeekday = lastDay.getDay();
  const offset = (lastWeekday - weekday + 7) % 7;
  const day = lastDay.getDate() - offset;
  return new Date(year, month, day);
}

/**
 * Check if a date is a US Federal Holiday
 */
export function isUSFederalHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const holidays = getUSFederalHolidays(year);
  
  return holidays.some(holiday => 
    holiday.getDate() === date.getDate() &&
    holiday.getMonth() === date.getMonth() &&
    holiday.getFullYear() === date.getFullYear()
  );
}

/**
 * Check if a date is a business day (not weekend or US federal holiday)
 */
export function isBusinessDay(date: Date): boolean {
  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  return !isWeekend && !isUSFederalHoliday(date);
}

/**
 * Calculate elapsed time between two dates in fractional days (based on 24-hour calendar time)
 * Useful for workflow stages where granularity below full days matters
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Fractional days (e.g., 0.5 for 12 hours)
 */
export function calculateFractionalDays(startDate: Date, endDate: Date): number {
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) return 0;
  
  const hours = diffMs / (1000 * 60 * 60);
  return Math.round((hours / 24) * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate the number of business days between two dates
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Number of business days (excluding weekends and US federal holidays)
 */
export function calculateBusinessDays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  // If start is after end, return negative count
  if (current > end) {
    return 0;
  }
  
  // Don't count the start date, start from next day
  current.setDate(current.getDate() + 1);
  
  while (current <= end) {
    if (isBusinessDay(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return count;
}

